// ============================================================================
// layoutVersionService — versioned canvas layouts for the Checklist Books.
// ----------------------------------------------------------------------------
// Each ChecklistBook has one or more immutable `layout_versions` docs. Every
// page in the book points at exactly one version via `layoutVersionId`. A
// layout edit forward-propagates:
//   1. mint a new version doc,
//   2. repoint the edited page + all future pages sharing the edited page's
//      current versionId to the new one,
//   3. stop at the first future page that already diverged.
//
// New-page creation calls `resolveInheritedVersion(bookId, pageKey)`, which
// walks BACKWARD from the requested key to the nearest prior page and adopts
// its versionId. Falls back to `book.activeLayoutVersionId` on the very first
// page of a fresh book.
//
// See src/types/checklistBooks.ts for the data shapes and rationale.
// ============================================================================

import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import {
  LAYOUT_VERSIONS_SUBCOL,
  PAGES_SUBCOL,
  type BookId,
  type ChecklistBook,
  type ChecklistBookPage,
  type ChecklistLayoutVersion,
  type ChecklistType,
  type LayoutResolutionResult,
  type LayoutVersionId,
  type PageKey,
  bookPath,
  layoutVersionPath,
  pagePath,
} from '../types/checklistBooks'
import {
  primeChecklistLayoutVersionCache,
  primeChecklistPageCache,
  readCachedLayoutVersion,
} from './checklistReadCache'

// ---------------------------------------------------------------------------
// Collection references
// ---------------------------------------------------------------------------

const bookRef = (bookId: BookId) => doc(firestoreDb, bookPath(bookId))
const pageRef = (bookId: BookId, pageKey: PageKey) =>
  doc(firestoreDb, pagePath(bookId, pageKey))
const layoutRef = (bookId: BookId, versionId: LayoutVersionId) =>
  doc(firestoreDb, layoutVersionPath(bookId, versionId))
const pagesCollectionRef = (bookId: BookId) =>
  collection(firestoreDb, `${bookPath(bookId)}/${PAGES_SUBCOL}`)
const layoutsCollectionRef = (bookId: BookId) =>
  collection(firestoreDb, `${bookPath(bookId)}/${LAYOUT_VERSIONS_SUBCOL}`)

// ---------------------------------------------------------------------------
// Actor context — every write records who did it (matches recurring service).
// ---------------------------------------------------------------------------

export type LayoutActor = {
  uid: string
  displayName?: string | null
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export const getLayoutVersion = async (
  bookId: BookId,
  versionId: LayoutVersionId,
): Promise<ChecklistLayoutVersion | null> => {
  return readCachedLayoutVersion(bookId, versionId, async () => {
    const snap = await getDoc(layoutRef(bookId, versionId))
    return snap.exists() ? (snap.data() as ChecklistLayoutVersion) : null
  })
}

/** Fetch several layout versions in parallel, deduped. Used by the canvas
 *  loader when a batch of pages references a small set of versions. */
export const getLayoutVersions = async (
  bookId: BookId,
  versionIds: readonly LayoutVersionId[],
): Promise<Record<LayoutVersionId, ChecklistLayoutVersion>> => {
  const unique = Array.from(new Set(versionIds))
  const results = await Promise.all(unique.map((id) => getLayoutVersion(bookId, id)))
  const out: Record<LayoutVersionId, ChecklistLayoutVersion> = {}
  unique.forEach((id, i) => {
    const v = results[i]
    if (v) out[id] = v
  })
  return out
}

// ---------------------------------------------------------------------------
// Version resolution for new-page creation
// ---------------------------------------------------------------------------

/**
 * Find the versionId a newly-created page at `pageKey` should adopt.
 *   1. Walk backward through existing pages ordered by `pageKey` DESC, stop
 *      at the first page whose `pageKey < requested`. Its `layoutVersionId`
 *      wins.
 *   2. If no prior page exists, fall back to `book.activeLayoutVersionId`.
 *   3. If neither exists, return `{ kind: 'none' }` — caller must mint a
 *      blank version.
 *
 * Note: `pageKey` string ordering matches chronological order because our
 * two variable formats — ISO date (YYYY-MM-DD) and ISO week (YYYY-Www) —
 * both sort lexicographically the way we want, and INDIVIDUAL books have a
 * single page ('single') so the walk isn't invoked for them.
 */
export const resolveInheritedVersion = async (
  bookId: BookId,
  pageKey: PageKey,
): Promise<LayoutResolutionResult> => {
  // 1. Nearest prior page.
  const priorQuery = query(
    pagesCollectionRef(bookId),
    where('pageKey', '<', pageKey),
    orderBy('pageKey', 'desc'),
    limit(1),
  )
  const priorSnap = await getDocs(priorQuery)
  if (!priorSnap.empty) {
    const priorPage = priorSnap.docs[0].data() as ChecklistBookPage
    if (priorPage.layoutVersionId) {
      return {
        kind: 'inherited',
        versionId: priorPage.layoutVersionId,
        sourcePageKey: priorPage.pageKey,
      }
    }
  }

  // 2. Fall back to the book's active version.
  const bookSnap = await getDoc(bookRef(bookId))
  const book = bookSnap.exists() ? (bookSnap.data() as ChecklistBook) : null
  if (book?.activeLayoutVersionId) {
    return { kind: 'active', versionId: book.activeLayoutVersionId }
  }

  return { kind: 'none' }
}

// ---------------------------------------------------------------------------
// Creation
// ---------------------------------------------------------------------------

type LayoutBody = Pick<ChecklistLayoutVersion, 'nodes' | 'wires'>

/**
 * Mint a brand-new layout version. Does NOT touch any page pointers or the
 * book's activeLayoutVersionId. Callers that want propagation use
 * `applyEditToPage` instead.
 */
export const createLayoutVersion = async (
  bookId: BookId,
  workspaceId: string,
  body: LayoutBody,
  actor: LayoutActor,
  opts: { parentVersionId?: LayoutVersionId | null } = {},
): Promise<LayoutVersionId> => {
  const newRef = doc(layoutsCollectionRef(bookId))
  const version: ChecklistLayoutVersion = {
    id: newRef.id,
    bookId,
    workspaceId,
    nodes: body.nodes ?? {},
    wires: body.wires ?? [],
    parentVersionId: opts.parentVersionId ?? null,
    createdAt: serverTimestamp(),
    createdById: actor.uid,
    createdByName: actor.displayName ?? null,
    recordVersion: 1,
  }
  const batch = writeBatch(firestoreDb)
  batch.set(newRef, version)
  await batch.commit()
  primeChecklistLayoutVersionCache(bookId, newRef.id, version)
  return newRef.id
}

/**
 * Ensure the book has an activeLayoutVersionId — creates an empty version
 * and points the book at it if not. Idempotent. Returns the resolved id.
 * Used when bootstrapping a fresh book.
 */
export const ensureActiveLayoutVersion = async (
  bookId: BookId,
  workspaceId: string,
  actor: LayoutActor,
): Promise<LayoutVersionId> => {
  const snap = await getDoc(bookRef(bookId))
  if (snap.exists()) {
    const book = snap.data() as ChecklistBook
    if (book.activeLayoutVersionId) return book.activeLayoutVersionId
  }
  const versionId = await createLayoutVersion(
    bookId,
    workspaceId,
    { nodes: {}, wires: [] },
    actor,
  )
  const batch = writeBatch(firestoreDb)
  const type: ChecklistType = bookId.endsWith('_DAILY') ? 'DAILY'
    : bookId.endsWith('_WEEKLY') ? 'WEEKLY'
    : 'INDIVIDUAL'
  const bookPayload: Record<string, unknown> = {
    activeLayoutVersionId: versionId,
    updatedAt: serverTimestamp(),
  }
  if (!snap.exists()) {
    bookPayload.id = bookId
    bookPayload.workspaceId = workspaceId
    bookPayload.checklistType = type
    bookPayload.createdAt = serverTimestamp()
    bookPayload.recordVersion = 1
  }
  batch.set(bookRef(bookId), bookPayload, { merge: true })
  await batch.commit()
  return versionId
}

// ---------------------------------------------------------------------------
// Edit + forward-propagation
// ---------------------------------------------------------------------------

/**
 * Apply a layout edit anchored at `pageKey`. Creates a new version whose body
 * is `nextBody`, then walks forward through pages of this book from `pageKey`
 * (inclusive) upward and repoints every consecutive page that still carries
 * the edited page's OLD versionId. Stops at the first page whose versionId
 * differs (already diverged).
 *
 * Returns the new versionId and the list of page keys that were repointed.
 *
 * All writes happen in a single batch — atomic.
 */
export const applyEditToPage = async (
  bookId: BookId,
  workspaceId: string,
  anchorPageKey: PageKey,
  nextBody: LayoutBody,
  actor: LayoutActor,
): Promise<{ versionId: LayoutVersionId; repointedPageKeys: PageKey[] }> => {
  // 1. Read the anchor page to discover the OLD versionId.
  const anchorSnap = await getDoc(pageRef(bookId, anchorPageKey))
  if (!anchorSnap.exists()) {
    throw new Error(`applyEditToPage: anchor page ${anchorPageKey} does not exist in book ${bookId}`)
  }
  const anchorPage = anchorSnap.data() as ChecklistBookPage
  // Anchor MUST already carry a layoutVersionId. Previously, an anchor with
  // no version fell through to `ensureActiveLayoutVersion(...)` and the
  // forward walk repointed every subsequent uninitialised page onto the
  // freshly-minted (empty) version — this compounded damage from Phase-1
  // corrupted pages, silently blanking their layouts across the whole
  // book. The `ensurePage` code path (checklistBookService) is the sole
  // authority for initialising a page's layoutVersionId; if an anchor
  // arrived here without one, either the corrupted-page repair script
  // hasn't run yet or a caller skipped the ensure step. Fail loud instead.
  if (typeof anchorPage.layoutVersionId !== 'string' || anchorPage.layoutVersionId.length === 0) {
    throw new Error(
      `applyEditToPage: anchor page ${anchorPageKey} in book ${bookId} has no layoutVersionId — call ensurePage(...) first or run scripts/repairCorruptedChecklistPages.ts`,
    )
  }
  const oldVersionId = anchorPage.layoutVersionId

  // 2. Read future pages (>= anchor by pageKey) in ascending order so we can
  //    stop at the first divergence.
  const forwardQuery = query(
    pagesCollectionRef(bookId),
    where('pageKey', '>=', anchorPageKey),
    orderBy('pageKey', 'asc'),
  )
  const forwardSnap = await getDocs(forwardQuery)

  const toRepoint: PageKey[] = [anchorPage.pageKey]
  const repointedPages: ChecklistBookPage[] = [anchorPage]
  for (const docSnap of forwardSnap.docs) {
    const page = docSnap.data() as ChecklistBookPage
    if (page.pageKey === anchorPage.pageKey) continue
    if (page.layoutVersionId !== oldVersionId) break
    toRepoint.push(page.pageKey)
    repointedPages.push(page)
  }

  // 3. Mint the new version + repoint pages in a single batch.
  const newRef = doc(layoutsCollectionRef(bookId))
  const newVersion: ChecklistLayoutVersion = {
    id: newRef.id,
    bookId,
    workspaceId,
    nodes: nextBody.nodes ?? {},
    wires: nextBody.wires ?? [],
    parentVersionId: oldVersionId,
    createdAt: serverTimestamp(),
    createdById: actor.uid,
    createdByName: actor.displayName ?? null,
    recordVersion: 1,
  }

  const batch = writeBatch(firestoreDb)
  batch.set(newRef, newVersion)

  for (const key of toRepoint) {
    batch.update(pageRef(bookId, key), {
      layoutVersionId: newRef.id,
      updatedAt: serverTimestamp(),
      lastUpdatedBy: actor.uid,
      lastUpdatedByName: actor.displayName ?? null,
    })
  }

  // The book's active pointer should reflect the freshest layout so brand-new
  // pages created after this edit inherit it (defensive; new-page creation
  // ALSO calls resolveInheritedVersion which finds the nearest prior page
  // anyway).
  batch.set(
    bookRef(bookId),
    {
      activeLayoutVersionId: newRef.id,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  )

  await batch.commit()
  primeChecklistLayoutVersionCache(bookId, newRef.id, newVersion)
  repointedPages.forEach((page) => {
    primeChecklistPageCache(bookId, page.pageKey, {
      ...page,
      layoutVersionId: newRef.id,
      lastUpdatedBy: actor.uid,
      lastUpdatedByName: actor.displayName ?? null,
    })
  })
  return { versionId: newRef.id, repointedPageKeys: toRepoint }
}

// ---------------------------------------------------------------------------
// Convenience — merge a per-page layoutPatch back into the base version.
// Used when a user's ad-hoc drag on a single page is later promoted to a
// full edit (turns the patch into a real version bump).
// ---------------------------------------------------------------------------

export const promotePagePatchToVersion = async (
  bookId: BookId,
  workspaceId: string,
  pageKey: PageKey,
  actor: LayoutActor,
): Promise<LayoutVersionId | null> => {
  const pageSnap = await getDoc(pageRef(bookId, pageKey))
  if (!pageSnap.exists()) return null
  const page = pageSnap.data() as ChecklistBookPage
  const patch = (page as ChecklistBookPage & { layoutPatch?: Record<string, { position?: { x: number; y: number }; hidden?: boolean }> }).layoutPatch
  if (!patch || Object.keys(patch).length === 0) return null

  const baseVersionId = page.layoutVersionId
    || await ensureActiveLayoutVersion(bookId, workspaceId, actor)
  const baseVersion = await getLayoutVersion(bookId, baseVersionId)
  if (!baseVersion) return null

  const mergedNodes: ChecklistLayoutVersion['nodes'] = { ...baseVersion.nodes }
  for (const [nodeId, delta] of Object.entries(patch)) {
    const base = mergedNodes[nodeId] ?? { checklistId: nodeId, position: { x: 0, y: 0 } }
    mergedNodes[nodeId] = {
      ...base,
      position: delta.position ?? base.position,
      hidden: delta.hidden ?? base.hidden,
    }
  }

  const { versionId } = await applyEditToPage(
    bookId,
    workspaceId,
    pageKey,
    { nodes: mergedNodes, wires: baseVersion.wires },
    actor,
  )

  // Clear the patch on the anchor page (which now points at the new version).
  const batch = writeBatch(firestoreDb)
  batch.update(pageRef(bookId, pageKey), {
    layoutPatch: {},
    updatedAt: serverTimestamp(),
    lastUpdatedBy: actor.uid,
    lastUpdatedByName: actor.displayName ?? null,
  })
  await batch.commit()
  primeChecklistPageCache(bookId, pageKey, {
    ...page,
    layoutVersionId: versionId,
    lastUpdatedBy: actor.uid,
    lastUpdatedByName: actor.displayName ?? null,
  })

  return versionId
}
